import {
  chain,
  flatMap,
  get,
  groupBy,
  map,
  mapValues,
  startCase,
  sum,
  sumBy,
  values,
} from 'lodash';

import type {
  AddItemPurchaseOrderReq,
  GetPurchaseOrderDetailsResponse,
  GroupUserAdvancements,
  ItemsRow,
} from '@appTypes/esb';
import { OrgActivitiesDashboard } from '@appTypes/esb/organizations/v2/_orgGuid/unitActivitiesDashboard';
import { getArnicaRootUrl } from '@config';
import {
  ProgramId,
  advancementTabNames,
  advancementTypes,
  ranksIds,
  unitTypeForProgramName,
  unitTypes,
} from '@modules/shared/constants';
import { getPreferredName } from '@modules/shared/utils/preferredName';

import { DenormalizedHistoryItem } from '../advancementHistory/utils';
import {
  type PocketCertificateType,
  linkedAdventuresAOL,
  linkedAdventuresBear,
  linkedAdventuresLion,
  linkedAdventuresTiger,
  linkedAdventuresWebelos,
  linkedAdventuresWolf,
  pocketCertificateTypes,
} from '../constants';
import type { RosterScout } from '../duck/types';
import { UNASSIGNED } from '../packRoster/constants';
import { PendingItemsTabs } from '../pendingItems/constants';
import { filterItems } from '../unitAdvancement/duck/utilsTyped';
import type { ItemsPerSubUnit, ToApproveItems } from '../unitAdvancement/types';
import {
  arnicaAPIPdfRankValues,
  groupByFilter,
  headerStatus,
} from './constants';
import type {
  DeleteItemPurchaseOrderReq,
  FilterPODetails,
  GroupByYouthUserAdvancements,
  ItemsRowGroup,
  POItemWithSubUnitDetails,
  selectedAdvancementsPendingItem,
} from './duck/types';

type getArnicaPDFParams = {
  api: string;
  poid: number;
  unitId: number;
  rankId?: number;
  isDateEarned?: number;
  isEsignature?: number;
  noDateOrSignature?: boolean;
  sku?: number;
  filterPODetails?: FilterPODetails;
};

export const getArnicaPDFUrl = (api: string, params: getArnicaPDFParams) => {
  const {
    poid,
    unitId,
    rankId,
    isDateEarned = 0,
    isEsignature = 0,
    sku,
    filterPODetails,
  } = params;
  const searchParams = new URLSearchParams();
  const rootURL = getArnicaRootUrl();

  let finalURL = `${rootURL}`;

  searchParams.append('Method', 'ExecuteContainerAPI');
  searchParams.append('Container', 'SB_PO_API');
  searchParams.append('API', api);

  searchParams.append('POID', poid.toString());
  searchParams.append('UnitID', unitId.toString());

  if (rankId) {
    searchParams.append('Rank', rankId.toString());
  }

  if (sku) {
    searchParams.append('sku', sku.toString());
  }

  if (filterPODetails) {
    let show = '';
    let groupBy = '';

    if (filterPODetails.sku) show += '@SKU';
    if (filterPODetails.prices) show += '@Price';
    if (filterPODetails.notes) show += '@Notes';

    searchParams.append('Show', show);

    if (!filterPODetails.noGrouping) {
      if (filterPODetails.youth) {
        groupBy += '@Youth';
      } else {
        groupBy += '@ItemType';
      }
    }
    if (filterPODetails.subUnit) groupBy += '@SubUnit';

    searchParams.append('GroupBy', groupBy);
  }

  searchParams.append('IsDateEarnedIncluded', isDateEarned.toString());
  searchParams.append('IsESignatureIncluded', isEsignature.toString());

  return (finalURL += `?${searchParams.toString()}`);
};

export const listOfSKU = {
  ADVENTURE: 646960,
  ADVENTURE_2: 661565,
  LION: 661560,
  LION_2: 661561,
  TIGER: 620807,
  TIGER_2: 620806,
  WOLF: 621021,
  BEAR: 646955,
  BEAR_2: 646954,
  WEBELOS: 645220,
  WEBELOS_2: 645219,
  ARROW: 661563,
  ARROW_2: 661564,
  MERIT_BADGE: 654936,
};

export const getSKU = (type: PocketCertificateType) => {
  switch (type) {
    case pocketCertificateTypes.ADVENTURE:
      return {
        first: listOfSKU.ADVENTURE,
        second: listOfSKU.ADVENTURE_2,
      };
    case pocketCertificateTypes.LION:
      return {
        first: listOfSKU.LION,
        second: listOfSKU.LION_2,
      };
    case pocketCertificateTypes.TIGER:
      return {
        first: listOfSKU.TIGER,
        second: listOfSKU.TIGER_2,
      };
    case pocketCertificateTypes.WOLF:
      return {
        first: listOfSKU.WOLF,
        second: null,
      };
    case pocketCertificateTypes.BEAR:
      return {
        first: listOfSKU.BEAR,
        second: listOfSKU.BEAR_2,
      };
    case pocketCertificateTypes.WEBELOS:
      return {
        first: listOfSKU.WEBELOS,
        second: listOfSKU.WEBELOS_2,
      };
    case pocketCertificateTypes.ARROW:
      return {
        first: listOfSKU.ARROW,
        second: listOfSKU.ARROW_2,
      };
    case pocketCertificateTypes.MERIT_BADGE:
      return {
        first: listOfSKU.MERIT_BADGE,
        second: null,
      };
    default:
      break;
  }
};

export const getAPIRankValue = (type: PocketCertificateType) => {
  switch (type) {
    case pocketCertificateTypes.TIGER:
      return arnicaAPIPdfRankValues.TIGER;
    case pocketCertificateTypes.WOLF:
      return arnicaAPIPdfRankValues.WOLF;
    case pocketCertificateTypes.BEAR:
      return arnicaAPIPdfRankValues.BEAR;
    case pocketCertificateTypes.LION:
      return arnicaAPIPdfRankValues.LION;
    case pocketCertificateTypes.WEBELOS:
      return arnicaAPIPdfRankValues.WEBELOS;
    case pocketCertificateTypes.ARROW:
      return arnicaAPIPdfRankValues.ARROW;
    default:
      return 0;
  }
};

export const getImgSrc = (sku: number) => {
  const url = `https://d3hdyt7ugiz6do.cloudfront.net/images/certificates/${sku}.png`;
  return url;
};

export const createPayloadAddItemPO = (
  organizationGuid: string,
  purchaseOrderId: number,
  selectedAdvancements: selectedAdvancementsPendingItem[],
): {
  organizationGuid: string;
  purchaseOrderId: number;
  body: AddItemPurchaseOrderReq[];
} => {
  const body = selectedAdvancements.map(adv => {
    const approachVersionId = [
      advancementTypes.RANKS,
      advancementTypes.MERIT_BADGES,
      advancementTypes.ADVENTURES,
    ];

    if (approachVersionId.includes(adv.advancementType)) {
      return {
        userId: adv.userId,
        advancementType: adv.advancementType,
        advancementVersionId: adv.versionId,
      };
    } else {
      return {
        userId: adv.userId,
        advancementType: adv.advancementType,
        userAwardId: adv.userAwardId,
      };
    }
  });

  return {
    organizationGuid: organizationGuid,
    purchaseOrderId: purchaseOrderId,
    body: body,
  };
};
const formatForAward = (
  formattedItems: DeleteItemPurchaseOrderReq,
  data: ItemsRow | ItemsRowGroup,
) => {
  const { advancementType, userAdvancementId } = data;
  const { advancementVersionId } = data as ItemsRowGroup;

  if (advancementType === advancementTypes.AWARDS) {
    formattedItems.userAwardId = userAdvancementId;
  } else {
    formattedItems.advancementVersionId = advancementVersionId;
  }
  return formattedItems;
};

export const createPayloadDeleteItemPO = (
  organizationGuid: string,
  purchaseOrderId: number,
  itemsRow: ItemsRow[] | ItemsRowGroup[],
): {
  organizationGuid: string;
  purchaseOrderId: number;
  body: DeleteItemPurchaseOrderReq[];
} => {
  let body: DeleteItemPurchaseOrderReq[] = [];

  itemsRow.forEach(adv => {
    const { advancementType, advancementId, members } = adv as ItemsRow;
    if (members) {
      members.forEach(member => {
        const formattedItems: DeleteItemPurchaseOrderReq = {
          userId: member.userId,
          advancementType,
          advancementId,
        };

        body = body.concat(formatForAward(formattedItems, member));
      });
    } else {
      const { userId } = adv as ItemsRowGroup;
      const formattedItems: DeleteItemPurchaseOrderReq = {
        userId,
        advancementType,
        advancementId,
      };
      body = body.concat(formatForAward(formattedItems, adv));
    }
  });

  return {
    organizationGuid: organizationGuid,
    purchaseOrderId: purchaseOrderId,
    body,
  };
};

export const createPayloadForSingleDeleteItemPO = (
  organizationGuid: string,
  purchaseOrderId: number,
  data: {
    youthUserId: number;
    advancementType: string;
    userAwardId?: number;
    id: number;
    activeVersionId: number;
  },
): {
  organizationGuid: string;
  purchaseOrderId: number;
  body: DeleteItemPurchaseOrderReq[];
} => {
  let body: DeleteItemPurchaseOrderReq[] = [];

  const { youthUserId, advancementType, id, userAwardId, activeVersionId } =
    data;

  const formattedItems: DeleteItemPurchaseOrderReq = {
    userId: youthUserId,
    advancementType,
    advancementId: id,
  };

  if (advancementType === advancementTypes.AWARDS) {
    formattedItems.userAwardId = userAwardId;
  } else {
    formattedItems.advancementVersionId = activeVersionId;
  }
  body = body.concat(formattedItems);

  return {
    organizationGuid: organizationGuid,
    purchaseOrderId: purchaseOrderId,
    body,
  };
};

export const getRankIdForLinkedAdventureId = (AdventureId: number) => {
  if (linkedAdventuresAOL.includes(AdventureId)) {
    return ranksIds.ARROW_OF_LIGHT;
  }
  if (linkedAdventuresWebelos.includes(AdventureId)) {
    return ranksIds.WEBELOS;
  }
  if (linkedAdventuresBear.includes(AdventureId)) {
    return ranksIds.BEAR;
  }
  if (linkedAdventuresWolf.includes(AdventureId)) {
    return ranksIds.WOLF;
  }
  if (linkedAdventuresTiger.includes(AdventureId)) {
    return ranksIds.TIGER;
  }
  if (linkedAdventuresLion.includes(AdventureId)) {
    return ranksIds.LION;
  }
};

export const orderItemsGrouped = (
  order: GetPurchaseOrderDetailsResponse,
): GroupUserAdvancements[] => {
  const { userAdvancements = [] } = order;
  const combinedItems = userAdvancements.reduce(
    (acc: GroupUserAdvancements[], item, currentIndex: number) => {
      const {
        userId,
        advancementType,
        id,
        userAwardId: userAdvancementId,
        versionId: advancementVersionId,
      } = item;
      const member = {
        name: getPreferredName(item),
        userId,
        advancementId: id,
        userAdvancementId,
        advancementVersionId,
      };
      // Check if SKU already exists in accumulator
      const existingItem = acc.find(i => i.sku === item.sku);
      if (existingItem !== undefined) {
        // SKU already exists, just update the members and totalPrice
        if (existingItem.members) existingItem.members.push(member);
        if (existingItem.totalPrice) existingItem.totalPrice += item.price;
        if (existingItem.quantity) existingItem.quantity++;
      } else {
        // SKU doesn't exist, create a new entry
        acc.push({
          name: item.name,
          sku: item.sku,
          members: [member],
          unitPrice: item.price,
          totalPrice: item.price,
          quantity: 1,
          advancementType,
          advancementId: id,
          key: currentIndex,
        });
      }
      return acc;
    },
    [],
  );

  return combinedItems;
};

export const getNotificationCountActivity = (
  unitActivitiesCount: OrgActivitiesDashboard,
) =>
  Object.values(unitActivitiesCount).reduce((acc, item) => {
    const property = Object.keys(item).find(key =>
      key.toLocaleLowerCase().includes('totalattendance'),
    );
    if (property) {
      return acc + item[property];
    }
    return acc + 0;
  }, 0);

export const getNotificationCount = (
  advancementTab: string,
  counts: {
    totalCounts: number;
    toApproveCount: number;
    toPurchaseCount: number;
    ranksCount: number;
    adventuresCount: number;
    meritBadgesCount: number;
    awardsCount: number;
    requirementsCount: number;
  },
) => {
  const forAdvancementDot =
    counts.requirementsCount +
    counts.awardsCount +
    counts.meritBadgesCount +
    counts.adventuresCount +
    counts.ranksCount;

  switch (advancementTab) {
    case PendingItemsTabs.RANKS:
      return counts.ranksCount;
    case PendingItemsTabs.ADVENTURES:
      return counts.adventuresCount;
    case PendingItemsTabs.MERIT_BADGES:
      return counts.meritBadgesCount;
    case PendingItemsTabs.AWARDS:
      return counts.awardsCount;
    case PendingItemsTabs.REQUIREMENTS:
      return counts.requirementsCount;
    case advancementTabNames.ADVANCEMENTS:
      return forAdvancementDot;
    default:
      return 0;
  }
};

export const filterRanksPackOrTroop = (
  items: DenormalizedHistoryItem[],
  unitTypeId: number,
) => {
  const isPack = ProgramId.CUB_SCOUT === unitTypeId;
  const isTroop = ProgramId.BOY_SCOUT === unitTypeId;

  let filteredRanks: DenormalizedHistoryItem[] = [];

  const troopAdvIds: number[] = [
    ranksIds.SCOUT,
    ranksIds.TENDERFOOT,
    ranksIds.SECOND_CLASS,
    ranksIds.FIRST_CLASS,
    ranksIds.STAR_SCOUT,
    ranksIds.LIFE_SCOUT,
    ranksIds.EAGLE_SCOUT,
  ];
  const packAdvIds: number[] = [
    ranksIds.TIGER,
    ranksIds.WOLF,
    ranksIds.BEAR,
    ranksIds.WEBELOS,
    ranksIds.ARROW_OF_LIGHT,
    ranksIds.BOBCAT,
    ranksIds.LION,
  ];

  const advIdsPackTroop = isPack ? packAdvIds : troopAdvIds;

  if (isPack || isTroop) {
    filteredRanks = items.filter(
      adv =>
        adv.advancementType === advancementTypes.RANKS &&
        advIdsPackTroop.includes(adv.id),
    );

    const excludedRanks = items.filter(
      adv => adv.advancementType !== advancementTypes.RANKS,
    );

    return excludedRanks.concat(filteredRanks);
  } else {
    return items;
  }
};

export const isArrayGroupBySubUnit = (items: ToApproveItems[]) =>
  items.some(item => (item as ItemsPerSubUnit).isHeader);

export const searchOnItems = (searchQuery: string, items: ToApproveItems[]) => {
  if (isArrayGroupBySubUnit(items)) {
    return (items as ItemsPerSubUnit[]).map(sub => ({
      ...sub,
      children: filterItems(sub.children, searchQuery),
    }));
  }

  if (searchQuery) {
    return filterItems(items, searchQuery);
  }

  return items;
};

export const achieveFirstClassAndAbove = (
  memberId: number,
  items: RosterScout[],
) => {
  const firstClassAboveRankIds: number[] = [
    ranksIds.FIRST_CLASS,
    ranksIds.STAR_SCOUT,
    ranksIds.LIFE_SCOUT,
    ranksIds.EAGLE_SCOUT,
  ];

  const personFound = items.find(person => person.memberId === memberId);

  const hasFirstClassOrHigher =
    personFound?.highestRanksApproved?.some(
      rank => rank.id !== undefined && firstClassAboveRankIds.includes(rank.id),
    ) ||
    personFound?.highestRanksAwarded?.some(
      rank => rank.id !== undefined && firstClassAboveRankIds.includes(rank.id),
    );

  return hasFirstClassOrHigher;
};

export const getProgramNameFromUnitLong = (unitLong: string) => {
  const index = unitLong.indexOf(' ');
  const unitType =
    index === -1
      ? unitLong
      : (unitLong.slice(0, index) as keyof typeof unitTypes);

  return get(unitTypeForProgramName, unitType);
};

export const implementGroupBySub = (
  allSubUnit: ItemsPerSubUnit[],
  advancementsWithMembers: GroupUserAdvancements[],
) => {
  const updatedList = allSubUnit
    .map(subUnit => {
      const children = advancementsWithMembers
        .map(adv => {
          const { members } = adv;

          if (!Array.isArray(members)) return;

          const matchingItems = members.filter(advMember =>
            subUnit.subRows.some(member => member.userId === advMember.userId),
          );

          return matchingItems.length > 0
            ? { ...adv, members: matchingItems }
            : null;
        })
        .filter(Boolean);

      return { ...subUnit, children };
    })
    .filter(sub => sub.children.length > 0);

  return updatedList;
};

const GroupByType = (formattedList: POItemWithSubUnitDetails[]) => {
  const groupedList = chain(formattedList)
    .groupBy('advancementType')
    .map(advancement => ({
      headerStatus: headerStatus.HEADER,
      subRows: chain(advancement)
        .groupBy('advancementId')
        .map(members => ({
          totalPrice: sumBy(members, 'unitPrice'),
          members: members.map(member => member),
          name: members[0].name,
          sku: members[0].sku,
          unitPrice: members[0].unitPrice,
          quantity: sumBy(members, 'quantity'),
          advancementType: members[0].advancementType,
          advancementId: members[0].advancementId,
          key: `${advancement[0].advancementType}__${members[0].advancementId}`,
          subUnitId: members[0].subUnitId,
          subUnitName: members[0].subUnitName,
          subUnitNameRaw: members[0].subUnitNameRaw,
        }))
        .value(),
      name: advancement[0].name,
      sku: advancement[0].sku,
      unitPrice: advancement[0].unitPrice,
      quantity: sumBy(advancement, 'quantity'),
      advancementType: advancement[0].advancementType,
      advancementId: advancement[0].advancementId,
      key: advancement[0].advancementType,
      // Need for Expand Pagination sync
      subUnitName: advancement[0].subUnitName,
      subUnitNameRaw: advancement[0].subUnitNameRaw,
      groupBy: groupByFilter.ITEM_TYPE,
      mainRowKey: advancement[0].advancementType,
      isMainRow: true,
      expanded: true,
      itemsCount: advancement.length,
      groupQuantity: advancement.length,
    }))
    .value();

  return groupedList;
};

const GroupByYouth = (
  formattedList: POItemWithSubUnitDetails[],
): GroupByYouthUserAdvancements[] =>
  map(groupBy(formattedList, 'userId'), group => ({
    key: `${group?.[0]?.userId}`,
    headerStatus: headerStatus.HEADER,
    userId: group?.[0]?.userId,
    youthName: group?.[0]?.youthName,
    subRows: group,
    // Need for Expand Pagination sync
    subUnitName: group?.[0]?.subUnitName,
    subUnitNameRaw: group?.[0]?.subUnitNameRaw,
    groupBy: groupByFilter.YOUTH,
    mainRowKey: group?.[0]?.userId,
    isMainRow: true,
    expanded: true,
    itemsCount: group.length,
    groupQuantity: group.length,
  }));

const GroupByItem = (
  formattedList: POItemWithSubUnitDetails[],
  subKey = '',
) => {
  const groupedList = chain(formattedList)
    .groupBy(item => `${item.advancementId}_${item.advancementType}`)
    .map(members => ({
      totalPrice: sumBy(members, 'unitPrice'),
      members: members.map(member => member),
      name: members[0].name,
      sku: members[0].sku,
      unitPrice: members[0].unitPrice,
      quantity: sumBy(members, 'quantity'),
      advancementType: members[0].advancementType,
      advancementId: members[0].advancementId,
      key: subKey
        ? `${subKey}__${members[0].advancementId}`
        : `${members[0].advancementType}__${members[0].advancementId}`,
    }))
    .value();

  return groupedList;
};

const GroupBySubUnitOnly = (formattedList: POItemWithSubUnitDetails[]) => {
  const reconstructFormat = chain(formattedList)
    .groupBy(sub => `${sub.subUnitName}__${sub.subUnitId}`)
    .map((sub, key) => {
      const [subUnitName, subUnitId] = key.split('__');
      return {
        key: subUnitId,
        headerStatus: headerStatus.HEADER,
        subUnitId: Number(subUnitId),
        subUnitName,
        subUnitNameRaw: subUnitName,
        groupBy: groupByFilter.SUB_UNIT,
        mainRowKey:
          subUnitId && subUnitId !== 'undefined' ? subUnitId : UNASSIGNED,
        isMainRow: true,
        expanded: true,
        itemsCount: sub.length,
        groupQuantity: sub.length,
        subRows: chain(sub)
          .groupBy(
            adv => `${subUnitId}__${adv.advancementType}__${adv.advancementId}`,
          )
          .map(adv => ({
            key: `${subUnitId}__${adv[0].advancementType}__${adv[0].advancementId}`,
            quantity: adv.length || 1,
            sku: adv[0].sku,
            name: adv[0].name,
            unitPrice: adv[0].unitPrice,
            totalPrice: adv[0].totalPrice * (adv.length || 1),
            members: adv,
            subUnitId: sub[0].subUnitId,
            subUnitName: sub[0].subUnitName,
            subUnitNameRaw: sub[0].subUnitNameRaw,
          }))
          .value(),
      };
    })
    .sortBy(sub =>
      sub.mainRowKey === UNASSIGNED ? '\uffff' : sub.mainRowKey.toLowerCase(),
    )
    .value();

  return reconstructFormat;
};

const GroupBySubUnitAndType = (formattedList: POItemWithSubUnitDetails[]) => {
  const groups = mapValues(groupBy(formattedList, 'subUnitId'), subUnit =>
    mapValues(groupBy(subUnit, 'advancementType'), advancementType =>
      sum(advancementType.map(({ quantity }) => quantity)),
    ),
  );

  const reconstructFormat = chain(formattedList)
    .groupBy(sub => `${sub.subUnitName}__${sub.subUnitId}`)
    .map((sub, key) => {
      const [subUnitName, subUnitId] = key.split('__');

      // Creation of Sub Header with Advancement List
      const groupedByType = groupBy(sub, 'advancementType');
      const children = flatMap(groupedByType, (advs, key) => [
        {
          headerStatus: headerStatus.SUB_HEADER,
          advancementType: key,
          key: `${subUnitId}__${key}`,
          groupQuantity: get(
            groups,
            subUnitId ? `${subUnitId}.${key}` : ['undefined'],
            0,
          ),
        },
        ...GroupByItem(advs, `${subUnitId}__${key}`),
      ]);

      // Display of Parent Header with Sub Header and Advancement
      return {
        headerStatus: headerStatus.PARENT_HEADER,
        key: subUnitId,
        subUnitId: Number(subUnitId),
        // Need for Expand Pagination sync
        subUnitName,
        subUnitNameRaw: sub[0].subUnitNameRaw,
        groupBy: groupByFilter.SUB_UNIT,
        mainRowKey:
          subUnitId && subUnitId !== 'undefined' ? subUnitId : UNASSIGNED,
        isMainRow: true,
        expanded: true,
        itemsCount: sum(values(get(groups, `${subUnitId}`))),
        groupQuantity: sum(values(get(groups, `${subUnitId}`))),
        subRows: children,
      };
    })
    .sortBy(sub =>
      sub.mainRowKey === UNASSIGNED ? '\uffff' : sub.mainRowKey.toLowerCase(),
    )
    .value();

  return reconstructFormat;
};

const GroupBySubUnitAndYouth = (formattedList: POItemWithSubUnitDetails[]) => {
  const groups = mapValues(groupBy(formattedList, 'subUnitId'), subUnit =>
    mapValues(groupBy(subUnit, 'userId'), user =>
      sum(user.map(({ quantity }) => quantity)),
    ),
  );

  const reconstructFormat = chain(formattedList)
    .groupBy(sub => `${sub.subUnitName}__${sub.subUnitId}`)
    .map((sub, key) => {
      const [subUnitName, subUnitId] = key.split('__');

      // Creation of Sub Header with Youth List
      const groupedByType = groupBy(sub, 'userId');
      const children = flatMap(groupedByType, (youth, userId) => [
        {
          key: `${subUnitId}-${userId}`,
          headerStatus: headerStatus.SUB_HEADER,
          youthName: youth[0].youthName,
          groupQuantity: get(groups, [subUnitId || '', userId], 0),
        },
        ...youth.map(adv => adv),
      ]);

      // Display of Parent Header with Sub Header and Youth
      return {
        headerStatus: headerStatus.PARENT_HEADER,
        key: subUnitId,
        subUnitId: Number(subUnitId),
        // Need for Expand Pagination sync
        subUnitName,
        subUnitNameRaw: sub[0].subUnitNameRaw,
        groupBy: groupByFilter.SUB_UNIT,
        mainRowKey:
          subUnitId && subUnitId !== 'undefined' ? subUnitId : UNASSIGNED,
        isMainRow: true,
        expanded: true,
        itemsCount: sum(values(get(groups, `${subUnitId}`))),
        groupQuantity: sum(values(get(groups, `${subUnitId}`))),
        subRows: children,
      };
    })
    .sortBy(sub =>
      sub.mainRowKey === UNASSIGNED ? '\uffff' : sub.mainRowKey.toLowerCase(),
    )
    .value();

  return reconstructFormat;
};

export const formatPOItemWithSubUnitDetails = (
  order: GetPurchaseOrderDetailsResponse,
  subUnitWithYouth: ItemsPerSubUnit[],
) => {
  const { userAdvancements = [] } = order;

  const youthWtihSubUnit = subUnitWithYouth.flatMap(sub =>
    sub.subRows.map(youth => ({
      ...youth,
      subUnitId: sub.subUnitId,
      subUnitName: sub.subUnitName,
      subUnitNameRaw: sub.subUnitNameRaw,
    })),
  );

  const formattedList = userAdvancements.map(user => {
    const subUnitDetails = youthWtihSubUnit.find(
      youth => youth.userId === user.userId,
    );

    return {
      key: `${user.advancementType}:${user.id}-uid:${user.userId}`,
      name: user.name,
      sku: user.sku,
      unitPrice: user.price,
      totalPrice: user.price,
      quantity: 1,
      advancementType: user.advancementType,
      advancementId: user.id,
      advancementVersionId: user.versionId,
      firstName: user.firstName,
      lastName: user.lastName,
      youthName: getPreferredName(user),
      userAdvancementId: user.userAwardId,
      userId: user.userId,
      subUnitId: subUnitDetails?.subUnitId,
      subUnitName: startCase(subUnitDetails?.subUnitName || 'unassigned'),
      subUnitNameRaw: startCase(subUnitDetails?.subUnitNameRaw || 'unassigned'),
    };
  });

  return formattedList;
};

export const performPOGroupBy = (
  order: GetPurchaseOrderDetailsResponse,
  subUnitWithYouth: ItemsPerSubUnit[],
  filterPODetails: FilterPODetails,
) => {
  const formattedList = formatPOItemWithSubUnitDetails(order, subUnitWithYouth);

  if (filterPODetails.subUnit) {
    if (filterPODetails.itemType) {
      const groupBySubUnitAndType = GroupBySubUnitAndType(formattedList);
      return groupBySubUnitAndType;
    }

    if (filterPODetails.youth) {
      const groupBySubUnitAndYouth = GroupBySubUnitAndYouth(formattedList);
      return groupBySubUnitAndYouth;
    }

    const groupBySubUnit = GroupBySubUnitOnly(formattedList);
    return groupBySubUnit;
  }

  if (filterPODetails.itemType) {
    const groupByType = GroupByType(formattedList);
    return groupByType;
  }

  if (filterPODetails.youth) {
    const groupByYouth = GroupByYouth(formattedList);
    return groupByYouth;
  }

  return GroupByItem(formattedList);
};
